import {PageHeader} from '../ui/PageHeader';
import { OperationsVisitChanges } from './OperationsVisitChanges';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api } from '../api';
import { useCapability, useStaffSession } from '../ui/StaffSession';
import { ErrorState } from '../ui/State';
import { formatDateOnly, formatDateTime, londonDateTimeInputInstant, dateTimeLocalInputValue } from '../dates';
import './engine-workspaces.css';
type Ref = { kind: string; id: string; methodProgrammeId: string | null; durationMinutes: null; purpose: string };
type Proposal = { buildingId: string; startsAt: string; endsAt: string; officerId: string | null; activities: Ref[] };
type Visit = {
  id: string;
  buildingId: string;
  version: number;
  booked: boolean;
  proposal?: Proposal;
  pendingChange?: any;
  readiness?: Record<string, { state: string }>;
  members: { reportId: string; reportHref: string; startsAt: string; endsAt: string; status: string }[];
};
type Source = { kind: string; id: string; title: string; template_id: string;due_on?:string|null };
type Candidate = { id: string; name: string; status: string; reasons: string[]; revision: string; activities: { requirement: string; availability: string }[] };
type Preview = {
  candidates: Candidate[];
  dependencies: Record<string, string>;
  coverage: string;
  activities: { title: string; durationMinutes: number; dueOn: string; sourceHref: string; startsAt: string; endsAt: string }[];
};
export function OperationsVisits() {
  const { id } = useParams(),
    [params] = useSearchParams(),
    session = useStaffSession(),
    manage = useCapability('field-reports:manage');
  const [visits, setVisits] = useState<Visit[]>([]),
    [current, setCurrent] = useState<Visit>(),
    [building, setBuilding] = useState(params.get('building') ?? ''),
    [starts, setStarts] = useState(''),
    [ends, setEnds] = useState(''),
    [refs, setRefs] = useState<Ref[]>([]),
    [officer, setOfficer] = useState<string | null>(null);
  const [sources, setSources] = useState<{ activities: Source[]; methods: { id: string; title: string; configuration: { templateId: string } }[] }>({
      activities: [],
      methods: []
    }),
    [preview, setPreview] = useState<Preview>(),
    [error, setError] = useState<unknown>(),
    [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(false),
    [outside, setOutside] = useState(false),
    [pending, setPending] = useState(false);
  const draftId = useRef(crypto.randomUUID()),
    retry = useRef<{ fingerprint: string; key: string } | undefined>(undefined),
    inspectionId = useRef(crypto.randomUUID());
  const load = async () => {
    const r = await api<{ visits: Visit[] }>('/api/operations/visits' + (id ? '/' + id : building ? '?building=' + building : ''));
    setVisits(r.visits);
    if (id) {
      const v = r.visits[0];
      setCurrent(v);
      if (v?.proposal) {
        setBuilding(v.buildingId);
        setStarts(dateTimeLocalInputValue(v.proposal.startsAt));
        setEnds(dateTimeLocalInputValue(v.proposal.endsAt));
        setRefs(v.proposal.activities);
        setOfficer(v.proposal.officerId);
      }
    }
  };
  useEffect(() => {
    void load().catch(setError);
  }, [id, building]);
  useEffect(() => {
    if (manage && building)
      void api<typeof sources>('/api/operations/visits/sources?building=' + building)
        .then(setSources)
        .catch(setError);
  }, [building, manage]);
  const proposal = (): Proposal => ({
    buildingId: building,
    startsAt: londonDateTimeInputInstant(starts)!,
    endsAt: londonDateTimeInputInstant(ends)!,
    officerId: officer,
    activities: refs
  });
  async function compare() {
    setError(undefined);
    try {
      setPreview(await api<Preview>('/api/operations/visits/candidates', { method: 'POST', body: JSON.stringify(proposal()) }));
    } catch (e) {
      setError(e);
      setPreview(undefined);
    }
  }
  async function command(command: string, extra: Record<string, unknown>) {
    setBusy(true);
    setError(undefined);
    setNotice('');
    try {
      const payload = { id: current?.id ?? draftId.current, expectedVersion: current?.version ?? 0, command, ...extra },
        fingerprint = JSON.stringify(payload);
      if (retry.current?.fingerprint !== fingerprint) retry.current = { fingerprint, key: crypto.randomUUID() };
      const result = await api<{ id: string }>('/api/operations/visits/commands', { method: 'POST', body: JSON.stringify({ ...payload, idempotencyKey: retry.current.key }) });
      retry.current = undefined;
      const r = await api<{ visits: Visit[] }>('/api/operations/visits/' + result.id);
      setCurrent(r.visits[0]);
      setNotice(command === 'Book' ? 'Attendance booked. Original activity reports are ready.' : 'Saved.');
      if (command !== 'Book') await compare();
      else setPreview(undefined);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  const changed = () => setPreview(undefined);
  return (
    <div className="engine-workspaces source-configuration-workspace">
      <PageHeader title={manage ? 'Plan attendance' : 'My visits'}/>
      {manage && <Link to={'/portfolio?lens=Operations' + (building ? '&building=' + building : '')}>Integrated oversight</Link>}
      <p>Each attendance groups original activity reports. Evidence and technical acceptance remain in their original workflows.</p>
      {notice && <p role="status">{notice}</p>}
      {error !== undefined && <ErrorState error={error} />}
      <section aria-label="Attendance register">
        <h2>{manage ? 'Saved attendance' : 'Your attendance'}</h2>
        {!visits.length&&<p>No attendance is recorded for this building.</p>}
        <ul>
          {visits.map((v) => (
            <li key={v.id}>
              <Link to={'/operations/visits/' + v.id}>{v.booked ? 'Booked attendance' : 'Unassigned / proposed attendance'}</Link>
              <ul>
                {v.members.map((m) => (
                  <li key={m.reportId}>
                    {formatDateTime(m.startsAt)} to {formatDateTime(m.endsAt)} · {m.status} · <Link to={m.reportHref}>Open activity report</Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </section>
      {current?.booked && (
        <section aria-label="Booked activity pack">
          <h2>Activity pack</h2>
          <p>Submission is not technical acceptance or source completion. Open an activity report for findings and remaining work.</p>
          <ul>
            {current.members.map((m) => (
              <li key={m.reportId}>
                {formatDateTime(m.startsAt)} to {formatDateTime(m.endsAt)} · {m.status} · <Link to={m.reportHref}>Open activity report</Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {manage && current?.booked && (
        <OperationsVisitChanges
          key={current.id}
          visit={current}
          onSaved={async () => {
            const r = await api<{ visits: Visit[] }>('/api/operations/visits/' + current.id);
            setCurrent(r.visits[0]);
            await load();
          }}
        />
      )}
      {manage && !current?.booked && (
        <>
          <section className="attendance-planning">
            <h2>Activities and window</h2>
            {!params.get('building') && !id && (
              <label>
                Building{' '}
                <select
                  value={building}
                  onChange={(e) => {
                    setBuilding(e.target.value);
                    setRefs([]);
                    changed();
                  }}
                >
                  <option value="">Choose a building</option>
                  {session?.buildings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Attendance starts{' '}
              <input
                type="datetime-local"
                value={starts}
                onChange={(e) => {
                  setStarts(e.target.value);
                  changed();
                }}
              />
            </label>
            <label>
              Attendance ends{' '}
              <input
                type="datetime-local"
                value={ends}
                onChange={(e) => {
                  setEnds(e.target.value);
                  changed();
                }}
              />
            </label>
            <fieldset>
              <legend>Commissioned activities</legend>
              {!sources.activities.length&&<p>No commissioned activities are available for this building.</p>}
              {sources.activities.map((s) => {
                const ref = refs.find((r) => r.kind === s.kind && r.id === s.id);
                return (
                  <div key={s.kind + s.id}>
                    <label>
                      <input
                        type="checkbox"
                        checked={Boolean(ref)}
                        onChange={(e) => {
                          setRefs((old) =>
                            e.target.checked
                              ? [...old, { kind: s.kind, id: s.id, methodProgrammeId: null, durationMinutes: null, purpose: 'General' }]
                              : old.filter((r) => r !== ref)
                          );
                          changed();
                        }}
                      />
                      {s.title} · {s.kind}{s.due_on?' · Due '+formatDateOnly(s.due_on):''}
                    </label>
                    {ref && !['Programme', 'Continuation'].includes(s.kind) && (
                      <label>
                        Approved source method for {s.title}
                        <select
                          value={ref.methodProgrammeId ?? ''}
                          onChange={(e) => {
                            setRefs((old) => old.map((r) => (r === ref ? { ...r, methodProgrammeId: e.target.value || null } : r)));
                            changed();
                          }}
                        >
                          <option value="">Choose the explicitly commissioned method</option>
                          {sources.methods
                            .filter((m) => m.configuration.templateId === s.template_id)
                            .map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.title}
                              </option>
                            ))}
                        </select>
                      </label>
                    )}
                  </div>
                );
              })}
            </fieldset>
            <label>
              New building inspection method{' '}
              <select
                value={refs.find((r) => r.kind === 'Inspection')?.methodProgrammeId ?? ''}
                onChange={(e) => {
                  setRefs((old) => [
                    ...old.filter((r) => r.kind !== 'Inspection'),
                    ...(e.target.value
                      ? [{ kind: 'Inspection', id: inspectionId.current, methodProgrammeId: e.target.value, durationMinutes: null, purpose: 'Initial building inspection' }]
                      : [])
                  ]);
                  changed();
                }}
              >
                <option value="">No additional inspection</option>
                {sources.methods.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title}
                  </option>
                ))}
              </select>
            </label>
            <div className="actions"><button type="button" className="secondary" disabled={busy || !building || !starts || !ends || !refs.length} onClick={() => void compare()}>
              Compare officers
            </button>
            <button type="button" disabled={busy || !preview} onClick={() => void command('SaveDraft', { proposal: proposal() })}>
              Save proposed attendance
            </button></div>
          </section>
          {preview && (
            <section aria-label="Officer comparison">
              <h2>Officer comparison</h2>
              <p>Coverage: {preview.coverage}. Availability does not certify professional competence. Choose any selectable officer.</p>
              <ul>
                {preview.candidates.map((c) => (
                  <li key={c.id}>
                    <label>
                      <input
                        type="radio"
                        name="officer"
                        disabled={c.status !== 'Selectable'}
                        checked={officer === c.id}
                        onChange={() => {
                          setOfficer(c.id);
                          setNotice('Save the selected officer before confirming readiness.');
                        }}
                      />
                      {c.name} · {c.status}
                    </label>
                    <p>{c.reasons.join(', ') || 'Reviewed time and current source requirements match.'}</p>
                    <p>{c.activities.map((a) => a.requirement + ' / ' + a.availability).join('; ')}</p>
                  </li>
                ))}
              </ul>
              <ul>
                {preview.activities.map((a, i) => (
                  <li key={i}>
                    {a.title} · {a.durationMinutes} minutes · {formatDateTime(a.startsAt)} to {formatDateTime(a.endsAt)} · <Link to={a.sourceHref}>Original source</Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {current && preview && (
            <section aria-label="Booking readiness">
              <h2>Confirm readiness</h2>
              {['site_access', 'tools', 'reporting'].map((check) => (
                <div key={check}>
                  <p>
                    {{ site_access: 'Keys and site access', tools: 'Tools and equipment', reporting: 'Reporting framework' }[check]} ·{' '}
                    {current.readiness?.[check]?.state ?? 'Unknown'}
                  </p>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const f = new FormData(e.currentTarget);
                      void command('SetReadiness', { check, state: f.get('state'), dependency: preview.dependencies[check], basis: f.get('basis') });
                    }}
                  >
                    <label>
                      {check.replaceAll('_', ' ')} state{' '}
                      <select name="state">
                        <option>Ready</option>
                        <option>Unknown</option>
                        <option>Blocked</option>
                      </select>
                    </label>
                    <label>
                      {check.replaceAll('_', ' ')} confirmation <input name="basis" required minLength={3} maxLength={500} />
                    </label>
                    <button disabled={busy}>Save {check.replaceAll('_', ' ')}</button>
                  </form>
                </div>
              ))}
              <label>
                <input type="checkbox" checked={outside} onChange={(e) => setOutside(e.target.checked)} />I have reviewed attendance outside the original due window
              </label>
              <label>
                <input type="checkbox" checked={pending} onChange={(e) => setPending(e.target.checked)} />I have reviewed pending availability warnings
              </label>
              <button
                type="button"
                disabled={busy || !preview.candidates.some((c) => c.id === officer && c.status === 'Selectable') || current.proposal?.officerId !== officer}
                onClick={() =>
                  void command('Book', {
                    assignedTo: officer,
                    selectionRevision: preview.candidates.find((c) => c.id === officer)?.revision,
                    acknowledgeOutsideDueWindow: outside,
                    acknowledgePendingLeave: pending
                  })
                }
              >
                Book attendance
              </button>
            </section>
          )}
        </>
      )}
    </div>
  );
}
